import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import debounce from 'lodash-es/debounce';
import { useScrollArrows } from 'site-modules/shared/hooks/use-scroll-arrows';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildShortsPath, MediaModel } from 'client/data/models/media';
import { SHORT_VIDEOS_MAP } from 'site-modules/core-page/constants/short-videos';
import { CorePageParams } from 'site-modules/shared/utils/core-page/params';
import { getStaticImageUrl, getYoutubePreviewImage, YOUTUBE_PREVIEW_SIZE } from 'client/utils/image-helpers';
import { stripHtml } from 'client/utils/string-utils';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { YoutubeVideoModalAsync } from 'site-modules/shared/components/youtube-video-modal/youtube-video-modal-async';
import { Link } from 'site-modules/shared/components/link/link';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';

import './video-reviews.scss';

const ARROW_CLASS_NAME =
  'arrow pos-a center-y d-flex align-items-center justify-content-center bg-white border-0 text-blue-40';

export function VideoReviewsUI({
  params,
  className = null,
  headingClassName = 'heading-4 mb-0_5',
  vehicleName = null,
  shortVideos = null,
  creativeId = null,
}) {
  const [modalVideoIndex, setModalVideoIndex] = useState(null);
  const thumbnailsRowRef = useRef();
  const [arrows, scrollLeft, scrollRight] = useScrollArrows(thumbnailsRowRef);

  const displayVideos = shortVideos?.length
    ? shortVideos
    : get(SHORT_VIDEOS_MAP, [params.make, params.model, params.year]);

  function onToggleVideoModal(event) {
    if (!get(event, 'currentTarget.dataset.index')) {
      setModalVideoIndex(null);
      return;
    }

    setModalVideoIndex(event.currentTarget.dataset.index);
  }

  const onScroll = debounce(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_VIEW_PHOTO,
        subaction_name: 'swipe_video_carousel',
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
        creative_id: creativeId,
      },
    });
  }, 400);

  if (!displayVideos?.length) {
    return null;
  }

  const modalVideoData = get(displayVideos, modalVideoIndex, {});

  return (
    <div className={classnames('video-reviews', className)} data-tracking-parent={creativeId}>
      <h2 className={headingClassName}>{vehicleName} Video Shorts</h2>

      <div className="medium">
        by{' '}
        <Link
          className="test-team-link text-primary-darker text-underline rounded"
          to="/car-reviews/features/how-we-test-cars-and-trucks.html"
        >
          Edmunds Test Team
        </Link>
      </div>
      <div className="thumbnails-wrapper pos-r">
        <button
          className={classnames(ARROW_CLASS_NAME, 'left left-0', {
            invisible: !arrows.left,
          })}
          onClick={scrollLeft}
          aria-label="Scroll videos left"
        >
          <i className="icon-arrow-left3" aria-hidden />
        </button>
        <ul
          ref={thumbnailsRowRef}
          onScroll={onScroll}
          className="thumbnails-row row flex-nowrap g-0_5 list-unstyled py-1"
          style={{ overflowX: 'auto' }}
        >
          {displayVideos.map(({ imageUrlPortrait, label, title, videoIdPortrait, videoDuration }, index) => {
            const thumbnailImage = imageUrlPortrait
              ? getStaticImageUrl(imageUrlPortrait)
              : getYoutubePreviewImage(videoIdPortrait, YOUTUBE_PREVIEW_SIZE.HQ);
            const displayLabel = title || label;

            return (
              <li key={videoIdPortrait} className="thumbnail-item">
                <button
                  onClick={onToggleVideoModal}
                  className="video-thumbnail bg-white w-100 h-100 py-1 px-1 rounded-8 d-flex"
                  data-index={index}
                  data-tracking-id="short_video_start"
                  data-tracking-value={label}
                  aria-label={title ? `Watch ${stripHtml(title)} video` : `Watch ${label} video review`}
                >
                  <div className="me-0_5" style={{ width: '100px', flexShrink: 0 }}>
                    <AspectRatioContainer className="rounded-8" aspectRatio="5:9">
                      <div
                        className="portrait-thumbnail pos-r h-100 d-flex justify-content-center"
                        style={imageUrlPortrait ? { backgroundImage: `url(${thumbnailImage})` } : undefined}
                      >
                        <img className="h-100" alt="" src={thumbnailImage} loading="lazy" />
                        <div className="overlay">
                          <div className="time-box rounded pos-a bottom-0 left-0 ms-0_5 mb-0_5 text-white bg-black fw-bold px-0_25 small">
                            <i className="icon-play41 size-10" aria-hidden /> {videoDuration}
                          </div>
                        </div>
                      </div>
                    </AspectRatioContainer>
                  </div>
                  <div className="label-text text-start">
                    <ContentFragment classes="fw-bold">{displayLabel}</ContentFragment>
                    <span className="text-nowrap small">Edmunds</span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        <button
          className={classnames(ARROW_CLASS_NAME, 'right right-0', {
            invisible: !arrows.right,
          })}
          onClick={scrollRight}
          aria-label="Scroll videos right"
        >
          <i className="icon-arrow-right3" aria-hidden />
        </button>
      </div>

      <YoutubeVideoModalAsync
        onToggleModal={onToggleVideoModal}
        isOpen={!!modalVideoData.videoIdPortrait}
        videoAspectRatio="9:16"
        videoId={modalVideoData.videoIdPortrait}
        videoTitle={modalVideoData.label}
        thumbnailSrc={getStaticImageUrl(get(modalVideoData, 'imageUrlPortrait', ''))}
        creativeId="video-reviews-modal"
        className="video-review-modal m-auto h-100 d-flex align-items-center justify-content-center"
        contentClassName="m-0 pb-3 mx-1_25"
        closeTrackingId="video_close"
        closeTrackingValue={modalVideoData.videoTitle}
        closeTrackingParent={`video-${modalVideoData.videoIdPortrait}`}
      />
    </div>
  );
}

VideoReviewsUI.propTypes = {
  params: CorePageParams.isRequired,
  shortVideos: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      title: PropTypes.string,
      imageUrlPortrait: PropTypes.string,
      videoIdPortrait: PropTypes.string,
      videoDuration: PropTypes.string,
    })
  ),
  className: PropTypes.string,
  headingClassName: PropTypes.string,
  vehicleName: PropTypes.string,
  creativeId: PropTypes.string,
};

export const VideoReviews = connectToModel(VideoReviewsUI, {
  shortVideos: bindToPath(({ params }) => buildShortsPath(params), MediaModel),
});
